import { SelectTypeWithChildren } from 'App/components/GenericSelectEntityModal/GenericSelectEntityModal';
import Client from 'App/resources/Model/Client';
import CustomField, { EntityCustomField } from 'App/resources/Model/CustomField';
import logError from 'Helpers/logError';
import moment from 'moment';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import LocalStorage from 'Components/Helpers/LocalStorage';
import ClientResource from 'App/resources/Client';
import ClientAttributes from 'App/babel/attributes/Client';
import { Feature, isAvailable, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import AppService from 'App/legacy/AppService';

export const formatWithSubAccounts = (
	accs: Client[] = [],
	translationFn: (langKey: string) => string,
	recent = false,
	asMainAccounts = false
): SelectTypeWithChildren<Client>[] | Client[] => {
	const mainAccounts = [];
	const subAccounts = [];
	const lang = {
		accounts: translationFn(recent ? 'default.latestAccounts' : 'default.accounts'),
		subaccounts: translationFn(recent ? 'default.latestSubaccounts' : 'account.subaccounts')
	};

	for (const acc of accs) {
		if (!asMainAccounts && acc.operationalAccount) {
			subAccounts.push(acc);
		} else {
			mainAccounts.push(acc);
		}
	}
	const formattedResults: SelectTypeWithChildren<Client>[] = [];
	if (mainAccounts.length) {
		formattedResults.push({
			name: lang.accounts,
			title: lang.accounts,
			icon: 'home',
			children: mainAccounts
		});
	}
	if (subAccounts.length) {
		formattedResults.push({
			name: lang.subaccounts,
			title: lang.subaccounts,
			icon: 'subaccounts',
			children: subAccounts
		});
	}
	return formattedResults;
};

export const getRelatedClientIds = (
	object: {
		clientConnection?: null | {
			id: number;
		};
		client: null | {
			operationalAccount: null | {
				id: number;
			};
		};
	},
	hasSubAccounts: boolean = true
) => {
	const relatedIds = [];

	if (object?.clientConnection?.id) {
		relatedIds.push(object.clientConnection.id);
	}
	if (hasSubAccounts && object?.client?.operationalAccount?.id) {
		relatedIds.push(object.client.operationalAccount.id);
	}

	return relatedIds;
};

export const binaryGroup = <T extends any>(objects: T[], predicate: (object: T) => boolean) =>
	objects.reduce<[T[], T[]]>(
		(res, object) => {
			const [truthy, falsey] = res;
			if (predicate(object)) {
				truthy.push(object);
			} else {
				falsey.push(object);
			}
			return [truthy, falsey];
		},
		[[], []]
	);

export const setCustomClientFieldsFilters = (reqBuilder: RequestBuilder, searchString: string) => {
	const searchableClientFields = ((AppService.getCustomFields('account') || []) as CustomField[])
		.filter(cf => cf.searchable)
		.map(field => field.id);

	if (searchableClientFields.length) {
		reqBuilder.next();
		const groupFilter = reqBuilder.groupBuilder();
		groupFilter.addFilter({ field: 'custom.fieldId' }, comparisonTypes.Equals, searchableClientFields);
		groupFilter.addFilter({ field: 'custom.value' }, comparisonTypes.Search, searchString);
		groupFilter.done();
		return true;
	}
	return false;
};

export const fetchClientsFromCustomFields = async (searchString: string) => {
	const { Account, AppService } = Tools;
	let clientIds: number[] = [];

	const accountFilter = new RequestBuilder();
	accountFilter.addFilter(Account.attr.active, comparisonTypes.Equals, 1);
	if (!setCustomClientFieldsFilters(accountFilter, searchString)) {
		return [];
	}

	try {
		const response = await Account.customer(AppService.getCustomerId()).find(accountFilter.build());
		clientIds = (response?.data || []).map((client: Client) => client.id);
	} catch (e) {
		logError(e, 'Failed to fetch clients');
	}

	return clientIds;
};

const UNSUPPORTED_SEARCHABLE_TYPES = new Set(['User', 'Users']);

export const getSearchableCustomFields = () => {
	const customFields = (AppService.getCustomFields('account') || []) as CustomField[];
	return customFields.filter(cf => cf.searchable && !UNSUPPORTED_SEARCHABLE_TYPES.has(cf.datatype));
};

const formatCustomFieldValue = (value: string, datatype: string): string => {
	if (datatype === 'Date') {
		const parsed = moment(value);
		return parsed.isValid() ? parsed.format('L') : value;
	}
	return value;
};

export const getSearchableCustomFieldValues = (
	custom: EntityCustomField[] | undefined,
	searchableFields?: CustomField[]
): { name: string; value: string }[] => {
	if (!custom?.length) {
		return [];
	}

	const fields = searchableFields ?? getSearchableCustomFields();
	if (!fields.length) {
		return [];
	}

	const searchableFieldIds = new Set(fields.map(f => f.id));
	const fieldNameMap = new Map(fields.map(f => [f.id, f.name]));
	const fieldTypeMap = new Map(fields.map(f => [f.id, f.datatype]));

	return custom
		.filter(cf => searchableFieldIds.has(cf.fieldId) && cf.value != null)
		.map(cf => ({
			name: fieldNameMap.get(cf.fieldId) || '',
			value: formatCustomFieldValue(String(cf.value), fieldTypeMap.get(cf.fieldId) || '')
		}));
};

export const fetchSubAccountIds = async (clientId: number): Promise<number[]> => {
	const hasSubAccountsV2 = hasSoftDeployAccess('SUB_ACCOUNTS_V2');
	const hasSubaccountFeature = isAvailable(Feature.SUB_ACCOUNTS) && hasSubAccountsV2;

	const localStorage = new LocalStorage();
	const includeSubAccount = localStorage.getValue<boolean>('includeSubaccountClientId' + clientId) ?? true;

	if ((!hasSubAccountsV2 && includeSubAccount) || hasSubaccountFeature) {
		try {
			const rb = new RequestBuilder();
			rb.fields = ['id'];
			rb.addFilter(ClientAttributes.operationalAccount.attr.id, comparisonTypes.Equals, clientId);
			const res = await ClientResource.find(rb.build());
			return res?.data?.map((client: Client) => client.id) ?? [];
		} catch (e) {
			logError(e, 'Failed to fetch sub-account IDs');
			return [];
		}
	}
	return [];
};
